import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Table, TableHead, TableRow, Paper, TableContainer, Link } from '@mui/material';
import { usePartnerFetchDealerDivisionsApi } from 'api/partner/dealer_divisions';
import {
  usePartnerBulkUpdateInstrumentsApi,
  usePartnerExportInstrumentsApi,
  usePartnerFetchInstrumentsApi,
} from 'api/partner/instrument';
import { InstrumentViewModel } from 'view_models/instrument';
import { usePartnerFetchInstrumentCategoriesApi } from 'api/partner/instrument_categories';
import { usePostExportInstrumentSharing } from 'api/partner/instruments/instrument_sharing_export';
import { usePartnerFetchLaboratoriesApi } from 'api/partner/laboratories';
import { usePartnerFetchOutsideLaboratoriesApi } from 'api/partner/outside_laboratories';
import { useFetchExColumnsApi } from 'api/shared/ex_columns';
import { NewDefectReportFormDialog } from 'components/partner/defect_report/defect_report_form_dialog';
import {
  NewInstrumentFormDialog,
  EditInstrumentFormDialog,
} from 'components/partner/instrument/instrument_form_dialog';
import { NewRepairEventFormDialog } from 'components/partner/repair_event/repair_event_form_dialog';
import { CheckboxFooter } from 'components/shared/checkbox_footer';
import { ContentHeader, ContentTitle } from 'components/shared/content';
import DropDownCheckbox from 'components/shared/dropdown_checkbox';
import { Flex } from 'components/shared/flex';
import { DropDownCheckboxItem } from 'components/shared/form/input';
import ImageView from 'components/shared/image_view';
import { BulkUpdateModal } from 'components/shared/instrument/bulk_update_modal';
import { DetailSearchModal, parseExColumnValues } from 'components/shared/instrument/detail_search_modal';
import { useModal, useMultipleModal } from 'components/shared/modal';
import Pagination from 'components/shared/pagination';
import { SearchBar } from 'components/shared/search_bar';
import {
  ExTableCell,
  OrderTableCell,
  AllCheckboxTableCell,
  CheckboxTableCell,
  ExTableBody,
} from 'components/shared/table';
import { DisplaySettingModal } from 'components/shared/table/display_setting';
import { TableExAttributesInfo } from 'components/shared/table_ex_attributes_info';
import ThemedButton from 'components/shared/themed_button';
import { PartnerStateContext } from 'contexts/partner_state_context';
import { BulkUpdateForm, Instrument, InstrumentSearchForm } from 'entities/instrument';
import { IndexApiState, Order, useForm } from 'rac';
import React, { useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PartnerRouteHelper } from 'routes/partner';
import { INDEX_TABLE_PADDING, getTableHeight } from 'utils/component_heights';
import { useCheckboxes, useDisplaySetting, useEffectSkipFirst } from 'utils/hooks/index';
import { booleanFilterOptions, booleanFilterValues } from 'utils/utils';

import './index.scss';
import { GlobalStateContext } from 'contexts/global_state_context';

const initialDisplaySettings = [
  { attr: 'createdAt', name: '登録日', visible: true },
  { attr: 'maker', name: 'メーカー', visible: true },
  { attr: 'outsideLaboratory', name: '顧客', visible: true },
  { attr: 'dealerDivision', name: '部署', visible: true },
];

export type InstrumentIndexProps = {
  roomId?: number;
};

const InstrumentIndex: React.FC = () => {
  const partnerState = useContext(PartnerStateContext);
  const search = useLocation().search;
  const query = new URLSearchParams(search);
  const navigate = useNavigate();
  const globalState = useContext(GlobalStateContext);

  const searchForm = useForm<InstrumentSearchForm>({
    q: {
      instrumentBasicInfoNameCont: query.get('name') ?? undefined,
      instrumentBasicInfoMakerCont: query.get('maker') ?? undefined,
      instrumentBasicInfoSerialNumberCont: query.get('serial_number') ?? undefined,
      instrumentBasicInfoManageNumberCont: query.get('manage_number') ?? undefined,
      instrumentBasicInfoModelNumberCont: query.get('model_number') ?? undefined,
      instrumentBasicInfoNoteCont: query.get('note') ?? undefined,
      instrumentBasicInfoInstrumentCategoryIdEq: query.get('category_id')
        ? Number(query.get('category_id'))
        : undefined,
    },
    searchText: query.get('q') || undefined,
    dealerDivisionIds: query.get('division')?.split(',') ?? undefined,
    requireProofreadFilter: query.get('proofread')?.split(',') ?? booleanFilterValues,
    sharingInstrumentFilter: query.get('sharing')?.split(',') ?? booleanFilterValues,
    expand: true,
    exColumnValues: parseExColumnValues(query),
  });
  const initialState: Partial<IndexApiState> = {
    ...(query.get('order') && { order: query.get('order') as Order }),
    ...(query.get('order_by') && { orderBy: query.get('order_by') as string }),
    ...(query.get('page') && { page: Number(query.get('page')) }),
    ...(query.get('perPage') && { perPage: Number(query.get('perPage')) }),
  };
  const instrumentIndexApi = usePartnerFetchInstrumentsApi(searchForm, initialState);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editInstrument, setEditInstrument] = React.useState<Instrument>({});
  const displaySettingSet = useDisplaySetting(initialDisplaySettings, 'partner-instrument-table');
  const categoryIndexApi = usePartnerFetchInstrumentCategoriesApi();
  const laboratoriesIndexApi = usePartnerFetchLaboratoriesApi({});
  const outsideLaboratoriesIndexApi = usePartnerFetchOutsideLaboratoriesApi({ useFilter: true });
  const dealerDivisionsIndexApi = usePartnerFetchDealerDivisionsApi(false);
  const exportApi = usePartnerExportInstrumentsApi(searchForm);
  const postExportInstrumentSharing = usePostExportInstrumentSharing();
  const newDefectReportModal = useMultipleModal<Instrument>();
  const newRepairEventModal = useMultipleModal<Instrument>();
  const exColumnApi = useFetchExColumnsApi('dealer_user');
  const checkboxSet = useCheckboxes(instrumentIndexApi.response?.instruments);
  const detailSearchModalState = useModal();
  const bulkUpdateModalState = useModal();
  const bulkUpdateInstrumentMutation = usePartnerBulkUpdateInstrumentsApi();

  useEffect(() => {
    instrumentIndexApi.execute();
    categoryIndexApi.execute();
    outsideLaboratoriesIndexApi.execute();
    laboratoriesIndexApi.execute();
    dealerDivisionsIndexApi.execute();
    exColumnApi.execute();
  }, []);

  useEffectSkipFirst(() => {
    pushHistory();
  }, [
    instrumentIndexApi.orderSet.order,
    instrumentIndexApi.orderSet.orderBy,
    instrumentIndexApi.pageSet.page,
    instrumentIndexApi.pageSet.perPage,
  ]);

  useEffectSkipFirst(() => {
    globalState.setLoading(bulkUpdateInstrumentMutation.isLoading);
  }, [bulkUpdateInstrumentMutation.isLoading]);

  const handleCreate = () => {
    instrumentIndexApi.execute();
  };

  const handleUpdate = () => {
    instrumentIndexApi.execute();
    setEditDialogOpen(false);
  };

  const handleDelete = () => {
    instrumentIndexApi.execute();
    setEditDialogOpen(false);
  };

  const handleClickEditButton = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, instrument: Instrument) => {
    setEditInstrument(instrument);
    setEditDialogOpen(true);
    e.stopPropagation();
  };

  const handleSearch = () => {
    instrumentIndexApi.executeAndPageReset();
    pushHistory();
  };

  const handleClickDetailButton = (instrument: Instrument) => {
    navigate(PartnerRouteHelper.instrumentShow(instrument.id!), { state: {} });
  };

  const handleClickExport = (format: 'xlsx') => {
    exportApi.execute(format);
  };

  const handleClickUnmanagedInstrumentsLink = () => {
    navigate(PartnerRouteHelper.unmanagedInstrumentIndex(), { state: {} });
  };

  const handleClickExportInstrumentSharing = () => {
    postExportInstrumentSharing.mutate({
      instrumentIds: checkboxSet.checkedIds,
    });
  };

  const handleDetailSearch = () => {
    detailSearchModalState.setOpen(false);
    instrumentIndexApi.executeAndPageReset();
    pushHistory();
  };

  const handleBulkUpdate = (values: BulkUpdateForm) => {
    bulkUpdateInstrumentMutation.mutate(values, {
      onSuccess: () => {
        globalState.setNotificationMessage({ body: '一括更新しました' });
        bulkUpdateModalState.setOpen(false);
        instrumentIndexApi.execute();
        checkboxSet.clear();
      },
      onError: () => {
        globalState.setNotificationMessage({ body: '一括更新に失敗しました', colorType: 'error' });
      },
    });
  };

  // 検索条件をURLに保持
  const pushHistory = () => {
    const order = instrumentIndexApi.orderSet.order;
    const orderBy = instrumentIndexApi.orderSet.orderBy;
    const page = instrumentIndexApi.pageSet.page;
    const perPage = instrumentIndexApi.pageSet.perPage;
    const params: { [key: string]: string } = {
      ...(searchForm.object.searchText && { q: searchForm.object.searchText }),
      ...(searchForm.object.dealerDivisionIds && { division: searchForm.object.dealerDivisionIds?.join(',') }),
      ...(searchForm.object.requireProofreadFilter && {
        proofread: searchForm.object.requireProofreadFilter?.join(','),
      }),
      ...(searchForm.object.sharingInstrumentFilter && {
        sharing: searchForm.object.sharingInstrumentFilter?.join(','),
      }),
      ...(searchForm.object.q.instrumentBasicInfoNameCont && { name: searchForm.object.q.instrumentBasicInfoNameCont }),
      ...(searchForm.object.q.instrumentBasicInfoMakerCont && {
        maker: searchForm.object.q.instrumentBasicInfoMakerCont,
      }),
      ...(searchForm.object.q.instrumentBasicInfoSerialNumberCont && {
        serial_number: searchForm.object.q.instrumentBasicInfoSerialNumberCont,
      }),
      ...(searchForm.object.q.instrumentBasicInfoManageNumberCont && {
        manage_number: searchForm.object.q.instrumentBasicInfoManageNumberCont,
      }),
      ...(searchForm.object.q.instrumentBasicInfoModelNumberCont && {
        model_number: searchForm.object.q.instrumentBasicInfoModelNumberCont,
      }),
      ...(searchForm.object.q.instrumentBasicInfoNoteCont && { note: searchForm.object.q.instrumentBasicInfoNoteCont }),
      ...(searchForm.object.q.instrumentBasicInfoInstrumentCategoryIdEq && {
        category_id: searchForm.object.q.instrumentBasicInfoInstrumentCategoryIdEq?.toString(),
      }),
      ...(searchForm.object.q.instrumentManagerIdEq && {
        manager_id: searchForm.object.q.instrumentManagerIdEq?.toString(),
      }),
      ...(order && orderBy && { order: order, order_by: orderBy }),
      ...(page && { page: page.toString() }),
      ...(perPage && { perPage: perPage.toString() }),
    };

    if (searchForm.object.exColumnValues) {
      Object.entries(searchForm.object.exColumnValues).forEach(([columnId, value]) => {
        if (value) {
          params[`ex_column_${columnId}`] = value.toString();
        }
      });
    }

    const queryParams = new URLSearchParams(params).toString();
    navigate(`${window.location.pathname}?${queryParams}`);
  };

  const dealerDivisionOptions = useMemo(
    (): DropDownCheckboxItem[] => [
      { value: '', label: '未選択' },
      ...dealerDivisionsIndexApi.response.dealerDivisions.map((division) => ({
        value: division.id!.toString(),
        label: division.name!,
      })),
    ],
    [dealerDivisionsIndexApi.response.dealerDivisions]
  );

  return (
    <>
      <EditInstrumentFormDialog
        onComplete={handleUpdate}
        onCompleteExcludeManagement={handleDelete}
        instrument={editInstrument}
        open={editDialogOpen}
        onCancel={() => setEditDialogOpen(false)}
        instrumentCategories={categoryIndexApi.response?.instrumentCategories}
        outsideLaboratories={outsideLaboratoriesIndexApi.response?.outsideLaboratories}
        onCompleteRemoveImage={instrumentIndexApi.execute}
      />
      <NewDefectReportFormDialog
        modalState={newDefectReportModal.modalState}
        instrumentId={newDefectReportModal.item.id}
        onComplete={() => newDefectReportModal.closeModal()}
      />
      <NewRepairEventFormDialog
        instrumentId={newRepairEventModal.item.id}
        modalState={newRepairEventModal.modalState}
        onComplete={() => newRepairEventModal.closeModal()}
        onClose={() => newRepairEventModal.closeModal()}
      />
      <DetailSearchModal
        modalState={detailSearchModalState}
        onSubmit={handleDetailSearch}
        searchForm={searchForm}
        instrumentCategories={categoryIndexApi.response?.instrumentCategories}
        exColumns={exColumnApi.response.exColumns}
      />
      <BulkUpdateModal
        modalState={bulkUpdateModalState}
        onSubmit={handleBulkUpdate}
        instrumentIds={checkboxSet.checkedIds}
        instrumentCategories={categoryIndexApi.response?.instrumentCategories}
        exColumns={exColumnApi.response.exColumns}
      />
      <ContentHeader
        subHeader={
          <>
            <ThemedButton
              variant="text"
              color="primary"
              onClick={handleClickUnmanagedInstrumentsLink}
              style={{ marginRight: 10 }}
            >
              管理対象外の機器一覧
            </ThemedButton>
            <DisplaySettingModal displaySettingSet={displaySettingSet} />
            <ThemedButton color="secondary" onClick={() => handleClickExport('xlsx')} style={{ marginLeft: 10 }}>
              Excel出力
            </ThemedButton>
          </>
        }
      >
        <ContentTitle title="機器一覧">
          <NewInstrumentFormDialog
            onComplete={handleCreate}
            instrumentCategories={categoryIndexApi.response?.instrumentCategories}
            outsideLaboratories={outsideLaboratoriesIndexApi.response?.outsideLaboratories}
            laboratories={laboratoriesIndexApi.response?.laboratories}
            exColumns={exColumnApi.response.exColumns}
          />
        </ContentTitle>
        <Flex alignItems="center">
          <SearchBar<InstrumentSearchForm>
            form={searchForm}
            searchAttr="searchText"
            placeholder="メーカー・製品名・型番・シリアル番号・管理番号で検索"
            onSubmit={handleSearch}
          />
          <ThemedButton
            color="secondary"
            onClick={() => detailSearchModalState.setOpen(true)}
            style={{ marginLeft: 10 }}
          >
            詳細検索
          </ThemedButton>
        </Flex>
      </ContentHeader>
      <Paper>
        <TableContainer style={{ maxHeight: getTableHeight(INDEX_TABLE_PADDING) }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <AllCheckboxTableCell checkboxSet={checkboxSet} />
                <ExTableCell width={150}></ExTableCell>
                <OrderTableCell
                  width={120}
                  open={displaySettingSet.isVisible('createdAt')}
                  align="center"
                  orderAttr="createdAt"
                  orderSet={instrumentIndexApi.orderSet}
                >
                  登録日
                </OrderTableCell>
                <OrderTableCell
                  width={120}
                  open={displaySettingSet.isVisible('maker')}
                  orderAttr="maker"
                  orderSet={instrumentIndexApi.orderSet}
                >
                  メーカー
                </OrderTableCell>
                <ExTableCell width={100} align="center" open={displaySettingSet.isVisible('outsideLaboratory')}>
                  顧客
                </ExTableCell>
                <ExTableCell width={120} open={displaySettingSet.isVisible('dealerDivision')}>
                  <DropDownCheckbox
                    form={searchForm}
                    attr="dealerDivisionIds"
                    name="dealerDivisionIds"
                    items={dealerDivisionOptions}
                    title="部署"
                    onSubmit={handleSearch}
                    btnStyle={{ padding: '4px 8px' }}
                    isLeft={true}
                    maxWidth={600}
                    showFilter
                    autocomplete
                  />
                </ExTableCell>
                <OrderTableCell minWidth={200} orderAttr="name" orderSet={instrumentIndexApi.orderSet}>
                  <div>
                    型番 / シリアル番号 / 管理番号 / 製品名
                    {exColumnApi.response.exColumns
                      .filter((col) => col.visibleOnTable)
                      .map((col) => (
                        <div style={{ fontSize: 12, lineHeight: '12px' }} key={`tablecol-${col.id}`}>
                          {col.name}
                        </div>
                      ))}
                  </div>
                </OrderTableCell>
                <ExTableCell width={120}>
                  <DropDownCheckbox
                    form={searchForm}
                    attr="requireProofreadFilter"
                    name="requireProofreadFilter"
                    items={booleanFilterOptions}
                    title="校正管理"
                    onSubmit={handleSearch}
                    btnStyle={{ padding: '4px 8px' }}
                    isLeft={true}
                    maxWidth={600}
                    showFilter
                  />
                </ExTableCell>
                {partnerState.currentDealer.authority?.enableInstrumentSharing && (
                  <ExTableCell width={120}>
                    <DropDownCheckbox
                      form={searchForm}
                      attr="sharingInstrumentFilter"
                      name="sharingInstrumentFilter"
                      items={booleanFilterOptions}
                      title="機器共有"
                      onSubmit={handleSearch}
                      btnStyle={{ padding: '4px 8px' }}
                      isLeft={true}
                      maxWidth={600}
                      showFilter
                    />
                  </ExTableCell>
                )}
                <ExTableCell width={100} align="center"></ExTableCell>
              </TableRow>
            </TableHead>
            <ExTableBody loading={instrumentIndexApi.loading}>
              {instrumentIndexApi.response?.instruments.map((instrument: Instrument, idx: number) => {
                const instrumentVM = new InstrumentViewModel(instrument);
                return (
                  <TableRow
                    key={idx}
                    hover={true}
                    onClick={() => handleClickDetailButton(instrument)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CheckboxTableCell checkboxesSet={checkboxSet} checkboxId={instrument.id!} />
                    <ExTableCell width={100} align="center">
                      <ImageView maxHeight={100} maxWidth={100} src={instrument.instrumentBasicInfo?.imageUrl} />
                    </ExTableCell>
                    <ExTableCell width={120} open={displaySettingSet.isVisible('createdAt')} align="center">
                      {instrumentVM.registeredAt()}
                    </ExTableCell>
                    <ExTableCell width={120} open={displaySettingSet.isVisible('maker')}>
                      {instrumentVM.getMakerName()}
                    </ExTableCell>
                    <ExTableCell width={120} open={displaySettingSet.isVisible('outsideLaboratory')}>
                      {instrumentVM.ownerableName()}
                    </ExTableCell>
                    <ExTableCell width={230} open={displaySettingSet.isVisible('dealerDivision')}>
                      {instrumentVM.dealerDivisionNames()}
                    </ExTableCell>
                    <ExTableCell>
                      <div style={{ fontSize: 11, marginBottom: 5 }}>{instrumentVM.getInstrumentNumbers()}</div>
                      <div style={{ fontSize: 16 }}>{instrumentVM.getInstrumentName()}</div>
                      <TableExAttributesInfo tableExAttrs={instrument.tableExAttrs || []} id={instrument.id} />
                      {instrument.instrumentBasicInfo?.externalLink &&
                        instrument.instrumentBasicInfo?.externalLinkName && (
                          <Link
                            target="_blank"
                            style={{ cursor: 'pointer' }}
                            href={instrument.instrumentBasicInfo?.externalLink}
                          >
                            {instrument.instrumentBasicInfo?.externalLinkName}
                          </Link>
                        )}
                    </ExTableCell>
                    <ExTableCell width={90} align="center">
                      {instrument.requireProofread && (
                        <Flex flexDirection="column" alignItems="center">
                          <div>
                            {instrument.isLastProofreadNg ? (
                              <label
                                style={{
                                  border: '2px solid #5DA2CA',
                                  color: '#5DA2CA',
                                  borderRadius: 5,
                                  padding: '2px 3px',
                                  fontSize: 12,
                                }}
                              >
                                不合格
                              </label>
                            ) : (
                              <CheckCircleOutlineIcon style={{ color: 'green' }} />
                            )}
                          </div>
                          <div style={{ lineHeight: 1.2 }}>{instrumentVM.getProofreadExpiredLabel()}</div>
                        </Flex>
                      )}
                    </ExTableCell>
                    {partnerState.currentDealer.authority?.enableInstrumentSharing && (
                      <ExTableCell width={90} align="center">
                        {instrument.sharingState === 'sharing' && <CheckCircleOutlineIcon style={{ color: 'green' }} />}
                      </ExTableCell>
                    )}
                    <ExTableCell width={100} align="center">
                      <Flex flexDirection="column">
                        <ThemedButton
                          size="small"
                          color="secondary"
                          onClick={(e) => handleClickEditButton(e, instrument)}
                        >
                          編集
                        </ThemedButton>
                      </Flex>
                    </ExTableCell>
                  </TableRow>
                );
              })}
            </ExTableBody>
          </Table>
        </TableContainer>
        <Pagination response={instrumentIndexApi.response} pageSet={instrumentIndexApi.pageSet} />
        <CheckboxFooter
          checkboxSet={checkboxSet}
          content={
            <>
              <ThemedButton
                color="primary"
                onClick={() => bulkUpdateModalState.setOpen(true)}
                style={{ marginRight: 12 }}
              >
                一括編集
              </ThemedButton>

              {partnerState.currentDealer.authority?.enableInstrumentSharing && (
                <ThemedButton color="primary" onClick={handleClickExportInstrumentSharing}>
                  機器共有インポートファイルの生成
                </ThemedButton>
              )}
            </>
          }
        />
      </Paper>
    </>
  );
};

export default InstrumentIndex;
